import * as React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import LoginPage from "./pages/Auth/LoginPage";
import { ThemeProvider } from "@mui/material/styles";
import { theme, darkTheme } from "./styles/theme";
import Container from "@mui/material/Container";
import AppTopBar from "./layouts/TopBar";
import Footer from "./layouts/Footer";
import { Provider } from "react-redux";
import { store } from "./redux-store";
import SocketConnection from "./SocketConnection";
import RouteLoginPage from "./pages/Auth/SelectRouteAndTrailerPage";
import I18nReduxLink from "./i18n/ReduxLink";
import NotebookPage from "./pages/NotebookPage/NotebookPage";
import * as featureActions from "./ducks/features";
import RoutePage from "./pages/RoutePage/RoutePage";
import WorktimePage from "./pages/WorktimePage/WorktimePage";
import {
  legacyNoTrailerBaseUrlString,
  legacyTruckBaseUrlString,
  routesLoginCompletedTrips,
  routesLoginUrl,
  truckBaseUrlString,
} from "./routes";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import AppUrlListener from "./AppUrlListener";
import { isMobile } from "./helpers/mobile";
import RegisterPushNotificationsListener from "./push-notifications/RegisterPushNotificationsListener";
import InAppNotifications from "./components/InAppNotifications/InAppNotifications";
import { ScanbotSDK, SdkConfiguration } from "capacitor-plugin-scanbot-sdk";
import * as Sentry from "@sentry/capacitor";
import CurrentTimeProvider from "./components/CurrentTimeProvider";
import NetworkStatusBanner from "./components/NetworkStatusBanner/NetworkStatusBanner";
import { enableEdgeToEdge } from "./edge-to-edge";
import { useEffect } from "react";
import NativeVersionCheck from "./components/NativeVersionCheck";
import { startLocationWatch } from "./helpers/mobile-position-watcher";
import { getBackgroundLocationWatch } from "./helpers/get-position-from-watch-or-demand";
import SwitchToNativeAppBanner from "./components/SwitchToNativeBanner";
import AppErrorBoundary from "./components/ErrorBoundary";
import ErrorToast from "./components/ErrorToast";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";

if (isMobile) {
  CapacitorUpdater.notifyAppReady();

  const scanbotLicense = (window as any).env?.scanbotApiKey;
  Sentry.captureMessage(
    `Scanbot license key ${scanbotLicense?.length} ${scanbotLicense?.slice(0, 4)}`
  );

  if (!scanbotLicense) {
    console.warn("Scanbot license key not found");
    Sentry.captureMessage("Scanbot license key not found");
  }

  // isMobile is a build-time flag; without the Capacitor native bridge every
  // native-only plugin call rejects with "not implemented on web".
  if (Capacitor.isNativePlatform()) {
    ScanbotSDK.initialize(
      new SdkConfiguration({
        licenseKey: scanbotLicense,
        storageImageQuality: 80,
      })
    )
      .then(() => console.log("Scanbot initialized"))
      .catch((err) => Sentry.captureException(err));
  } else {
    Sentry.captureMessage(
      "Mobile build running without the Capacitor native bridge",
      "warning"
    );
  }
}

// Layout route for the driving/worktime pages. Because its path declares
// :routeId/:truckId/:trailerId, everything it renders (the page via <Outlet/>
// and the <Footer/>) can read those params via useParams().
function DriverLayout() {
  return (
    <>
      <Grid>
        <Outlet />
      </Grid>
      <Grid>
        <Footer />
      </Grid>
    </>
  );
}

// Redirects the legacy URL shapes (the pre-sessionless one with a
// /sessions/:sessionId/ segment, and the pre-trailer one without the
// /trailer/:trailerId segment) to the current shape, preserving the page and
// query string. The trailer is unknown on those URLs, so it becomes "null".
// Exists so an over-the-air update doesn't strand drivers who were already on
// such a URL. See the legacy*UrlString constants in routes.ts.
function LegacyUrlRedirect() {
  const { routeId, truckId } = useParams();
  const location = useLocation();
  const tail = location.pathname.endsWith("/worktime") ? "worktime" : "driving";
  return (
    <Navigate
      replace
      to={`/routes/${routeId}/truck/${truckId}/trailer/null/${tail}${location.search}`}
    />
  );
}

function App() {
  const [darkMode, setDarkMode] = React.useState(false);

  store.dispatch(featureActions.initialize());

  document.addEventListener("visibilitychange", () => {
    store.dispatch(featureActions.initialize());
  });

  useEffect(() => {
    if (isMobile) {
      enableEdgeToEdge();
      if (Capacitor.isNativePlatform()) {
        store
          .dispatch(featureActions.initialize())
          .then(() => startLocationWatch(getBackgroundLocationWatch()))
          .catch((err) => Sentry.captureException(err));
      }
    }
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const handleUpdates = async () => {
      // Download new update in background for next launch
      // (Applying pending updates is handled at module level before React renders)
      try {
        const latest = await CapacitorUpdater.getLatest();
        if (latest && latest.url) {
          console.log("Downloading update:", latest.version);
          const bundle = await CapacitorUpdater.download({
            url: latest.url,
            version: latest.version,
          });
          await Preferences.set({
            key: "pending_bundle",
            value: JSON.stringify({ id: bundle.id, version: bundle.version }),
          });
          console.log("Update downloaded, will apply on next launch");
        }
      } catch (e) {
        // No update available or offline
      }
    };

    handleUpdates();
  }, []);

  return (
    <Provider store={store}>
      <I18nReduxLink />
      <SocketConnection>
      <ThemeProvider theme={darkMode ? darkTheme : theme}>
        <CssBaseline />
        <ErrorToast />
        <AppErrorBoundary>
          <CurrentTimeProvider>
            <Router>
              {isMobile && <RegisterPushNotificationsListener />}
              {isMobile && <AppUrlListener />}
              {isMobile && <NativeVersionCheck />}
              <Grid
                container
                direction="column"
                style={{
                  ...(isMobile && {
                    paddingTop: "env(safe-area-inset-top)",
                    paddingBottom: "env(safe-area-inset-bottom)",
                  }),
                }}
              >
                <Grid
                  style={{
                    position: "sticky",
                    top: isMobile ? "env(safe-area-inset-top)" : 0,
                    zIndex: 1,
                  }}
                >
                  <InAppNotifications />
                  <AppTopBar darkMode={darkMode} setDarkMode={setDarkMode} />
                  <NetworkStatusBanner />
                  <SwitchToNativeAppBanner />
                </Grid>
                <Grid>
                  <Container component="main" maxWidth="sm" sx={{ mt: "3rem" }}>
                    <AppErrorBoundary>
                      <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route
                          path="*"
                          element={
                            <Routes>
                              <Route
                                path={routesLoginUrl}
                                element={<RouteLoginPage />}
                              />
                              <Route
                                path={routesLoginCompletedTrips}
                                element={<RouteLoginPage showCompletedTrips />}
                              />
                              <Route
                                path={truckBaseUrlString}
                                element={<DriverLayout />}
                              >
                                <Route
                                  path="driving"
                                  element={<RoutePage />}
                                />
                                <Route
                                  path="worktime"
                                  element={<WorktimePage />}
                                />
                              </Route>
                              {/* Redirect stale OTA URLs (the old
                                  /sessions/:sessionId/ shape and the old shape
                                  without /trailer/:trailerId) to the new
                                  shape, instead of leaving them on a dead
                                  route. */}
                              <Route
                                path={`${legacyTruckBaseUrlString}/driving`}
                                element={<LegacyUrlRedirect />}
                              />
                              <Route
                                path={`${legacyTruckBaseUrlString}/worktime`}
                                element={<LegacyUrlRedirect />}
                              />
                              <Route
                                path={`${legacyNoTrailerBaseUrlString}/driving`}
                                element={<LegacyUrlRedirect />}
                              />
                              <Route
                                path={`${legacyNoTrailerBaseUrlString}/worktime`}
                                element={<LegacyUrlRedirect />}
                              />
                            </Routes>
                          }
                        />
                        <Route
                          path="/driver-notebook"
                          element={<NotebookPage />}
                        />
                        <Route
                          path="/"
                          element={<Navigate to="/routes/login" />}
                        />
                      </Routes>
                    </AppErrorBoundary>
                  </Container>
                </Grid>
              </Grid>
            </Router>
          </CurrentTimeProvider>
        </AppErrorBoundary>
      </ThemeProvider>
      </SocketConnection>
    </Provider>
  );
}

export default App;
